'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useId, useState } from 'react';
import { FormErrorBox } from '../FormErrorBox';
import { validateDegree } from '../validation';
import type { MessageKey } from '../validation';
import type { StepProps } from './stepProps';
import { StepShell } from './StepShell';
import {
  formSelectBaseClass,
  SelectChevron,
} from '@/components/form/SelectField';

/**
 * Step 3 — Abschluss (AC 4). Single dropdown.
 *
 * `value` is what the NHL-Tool receives as `qualification` and is deliberately
 * NOT translated: the endpoint has no enum for it and stores whatever string it
 * is sent (spec § Assumptions), so a French visitor must still submit the
 * German term. Only `labelKey` varies by locale.
 */
export const DEGREE_OPTIONS = [
  { value: 'Abitur oder Fach-Abitur', labelKey: 'degreeAbitur' },
  { value: 'Bachelor oder vergleichbar', labelKey: 'degreeBachelor' },
  { value: 'Master oder vergleichbar', labelKey: 'degreeMaster' },
  { value: 'Dr.', labelKey: 'degreeDoctorate' },
  { value: 'Anderer Abschluss / Sonstige', labelKey: 'degreeOther' },
] as const;

export const Step3Degree = ({
  state,
  dispatch,
  chrome,
  onBack,
  onNext,
}: StepProps) => {
  const t = useTranslations('tutorApplication');
  const errorId = useId();
  const [errorKey, setErrorKey] = useState<MessageKey | undefined>(undefined);

  const handleNext = () => {
    const problem = validateDegree(state);
    setErrorKey(problem);
    if (!problem) {
      onNext();
    }
  };

  return (
    <StepShell
      heading={t('step3Heading')}
      chrome={chrome}
      onBack={onBack}
      onNext={handleNext}
      hasError={Boolean(errorKey)}
    >
      {errorKey && <FormErrorBox id={errorId}>{t(errorKey)}</FormErrorBox>}

      {/*
        The shared class rather than a hand-rolled one, so this select cannot
        drift from the rest of the forms — which is how it ended up the only
        control still at 14px and zooming on iOS (SH-480). This form is
        reducer-based, so it cannot mount `<SelectField>` itself; it shares the
        class and the chevron instead. `relative` + `pr-10` are what that
        chevron needs, since the class is `appearance-none`.
      */}
      <div className="relative">
        <select
          aria-label={t('degreeLabel')}
          aria-invalid={Boolean(errorKey)}
          aria-describedby={errorKey ? errorId : undefined}
          className={classNames(formSelectBaseClass, 'pr-10')}
          value={state.degree}
          onChange={(e) => {
            setErrorKey(undefined);
            dispatch({ type: 'UPDATE_DEGREE', degree: e.target.value });
          }}
        >
          <option value="">{t('degreePlaceholder')}</option>
          {DEGREE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
        <SelectChevron />
      </div>
    </StepShell>
  );
};
